import React, { useEffect, useMemo, useState, useCallback } from "react";
import api, { formatApiError, downloadBlob } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  CalendarClock, ChevronLeft, ChevronRight, AlertTriangle, Plus, Trash2,
  Umbrella, Users, UserCheck, UserX, Heart, Activity, Search, Filter, Printer,
  Send, Copy, FileSpreadsheet, Sparkles, ShieldAlert, Clock, TrendingUp, GripVertical,
  Building2, ChevronDown,
} from "lucide-react";
import PersonAvatar from "@/components/PersonAvatar";
import {
  DndContext, PointerSensor, useSensor, useSensors, useDraggable, useDroppable, DragOverlay,
  closestCenter,
} from "@dnd-kit/core";
import RotaAiOptimiser from "@/components/RotaAiOptimiser";
import PayrollWidget from "@/components/PayrollWidget";
import MobileStaffScheduleAgenda from "@/components/mobile/MobileStaffScheduleAgenda";

// --- utils ---------------------------------------------------------------
// Timezone-safe date helpers — do NOT reintroduce `.toISOString().slice(0, 10)`
// here. See lib/dateWeek.js and lib/__tests__/dateWeek.test.js for the fix
// history (the rota week-picker was sending Sun instead of Mon in UTC+
// browsers, cascading into the "only 1 of 5 shifts reached payroll" bug).
import { mondayOf, addDays, todayISO } from "@/lib/dateWeek";

function timeHM(iso) {
  if (!iso) return "";
  return iso.slice(11, 16);
}
function overlaps(a, b) {
  const as = new Date(a.scheduled_start);
  const ae = a.scheduled_end ? new Date(a.scheduled_end) : new Date(as.getTime() + 30 * 60000);
  const bs = new Date(b.scheduled_start);
  const be = b.scheduled_end ? new Date(b.scheduled_end) : new Date(bs.getTime() + 30 * 60000);
  return as < be && bs < ae;
}
function hoursOf(v) {
  if (!v.scheduled_start || !v.scheduled_end) return 0.5;
  const s = new Date(v.scheduled_start).getTime();
  const e = new Date(v.scheduled_end).getTime();
  return Math.max(0, (e - s) / 3600000);
}
function shiftType(v) {
  const h = parseInt((v.scheduled_start || "").slice(11, 13), 10);
  if (isNaN(h)) return "unspecified";
  if (h >= 6 && h < 12) return "morning";
  if (h >= 12 && h < 17) return "afternoon";
  if (h >= 17 && h < 22) return "evening";
  return "night";
}
const SHIFT_STYLE = {
  morning: "bg-amber-50 border-amber-300 text-amber-900 dark:bg-amber-950/40 dark:border-amber-800 dark:text-amber-200",
  afternoon: "bg-sky-50 border-sky-300 text-sky-900 dark:bg-sky-950/40 dark:border-sky-800 dark:text-sky-200",
  evening: "bg-violet-50 border-violet-300 text-violet-900 dark:bg-violet-950/40 dark:border-violet-800 dark:text-violet-200",
  night: "bg-slate-800 border-slate-900 text-white",
  unspecified: "bg-slate-50 border-slate-300 text-slate-800",
};
const SHIFT_LABEL = { morning: "Morning", afternoon: "Afternoon", evening: "Evening", night: "Night" };

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const WEEKLY_HOURS_LIMIT = 48; // Working Time Regulations

// --- component ----------------------------------------------------------
export default function StaffSchedule() {
  const { user } = useAuth();
  const canManage = user?.role === "admin" || user?.role === "manager";

  const [weekStart, setWeekStart] = useState(mondayOf());
  const [staff, setStaff] = useState([]);
  const [visits, setVisits] = useState([]);
  const [timeOff, setTimeOff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [shiftFilter, setShiftFilter] = useState("all");
  const [availability, setAvailability] = useState("all");
  const [toOpen, setToOpen] = useState(false);
  const [toForm, setToForm] = useState({ staff_user_id: "", start_date: "", end_date: "", kind: "holiday", notes: "" });
  const [toSaving, setToSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [activeDrag, setActiveDrag] = useState(null);
  const [nearest, setNearest] = useState(null);
  const [heatmap, setHeatmap] = useState({});
  const [clients, setClients] = useState([]);
  const [sites, setSites] = useState([]);
  const [filterSite, setFilterSite] = useState("all");
  const [coverage, setCoverage] = useState(null);
  const [coverageOpen, setCoverageOpen] = useState(false);
  const [coverageLoading, setCoverageLoading] = useState(false);
  const [newShift, setNewShift] = useState(null);
  const [savingShift, setSavingShift] = useState(false);
  // Batch 4.1 — mobile / tablet ( <lg ) presentation state. Desktop is
  // NOT affected by these: they only drive the `lg:hidden` mobile agenda.
  const [selectedDay, setSelectedDay] = useState(todayISO());
  const [viewShift, setViewShift] = useState(null);
  // Delete-shift confirmation. Holds the shift being confirmed; null when closed.
  const [askDeleteShift, setAskDeleteShift] = useState(null);
  const [deletingShift, setDeletingShift] = useState(false);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  // Sites the current user is authorised to select on the New Shift form.
  // Admin ("All sites", site_id null) sees every site; a site-scoped user
  // may only pick their assigned site.
  const accessibleSites = useMemo(() => {
    if (!user?.site_id) return sites;
    return sites.filter((s) => s.id === user.site_id);
  }, [sites, user]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const end = addDays(weekStart, 6);
      // Widen the visit fetch to ±6 weeks so we can render the 13-week
      // heatmap strip + derive nearest-populated-week in a single call.
      const windowStart = addDays(weekStart, -42);
      const windowEnd = addDays(weekStart, 48);
      const [u, v, off, c, st] = await Promise.all([
        api.get("/auth/users"),
        api.get(`/visits?start_date=${windowStart}&end_date=${windowEnd}`),
        api.get(`/staff-ext/time-off?start_date=${weekStart}&end_date=${end}`).catch(() => ({ data: [] })),
        api.get("/clients").catch(() => ({ data: [] })),
        api.get("/sites").catch(() => ({ data: [] })),
      ]);
      const allWindow = v.data || [];
      const weekLo = weekStart;
      const weekHi = end;
      const weekVisits = allWindow.filter((x) => {
        const day = (x.scheduled_start || "").slice(0, 10);
        return day >= weekLo && day <= weekHi;
      });
      setStaff((u.data || []).filter((x) => ["care_worker", "manager"].includes(x.role)));
      setVisits(weekVisits);
      setTimeOff(off.data || []);
      setClients(c.data || []);
      setSites(st.data || []);

      // Build heatmap: {mondayISO: count} for every week represented.
      const hm = {};
      for (const vv of allWindow) {
        const day = (vv.scheduled_start || "").slice(0, 10);
        if (!day) continue;
        const wm = mondayOf(day);
        hm[wm] = (hm[wm] || 0) + 1;
      }
      setHeatmap(hm);

      // Nearest week with shifts (relative to the currently viewed week) —
      // used by the empty-state jump button.
      if (weekVisits.length === 0 && allWindow.length > 0) {
        const anchor = new Date(weekStart + "T00:00:00").getTime();
        let best = allWindow[0];
        let bestDelta = Infinity;
        for (const vv of allWindow) {
          const t = new Date((vv.scheduled_start || "").slice(0, 10) + "T00:00:00").getTime();
          const delta = Math.abs(t - anchor);
          if (delta < bestDelta) { bestDelta = delta; best = vv; }
        }
        const bestDay = (best.scheduled_start || "").slice(0, 10);
        setNearest(bestDay ? { weekStart: mondayOf(bestDay), date: bestDay } : null);
      } else {
        setNearest(null);
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [weekStart]);
  useEffect(() => { load(); }, [load]);

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);
  const weekEnd = days[6];
  const today = todayISO();

  // Filtered staff based on search + role
  const filteredStaff = useMemo(() => {
    let list = staff;
    if (roleFilter !== "all") list = list.filter((s) => s.role === roleFilter);
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      list = list.filter((s) => (s.name || s.email || "").toLowerCase().includes(q));
    }
    if (availability === "available") {
      list = list.filter((s) => !timeOff.some((t) => t.staff_user_id === s.id && t.start_date <= weekEnd && t.end_date >= weekStart));
    }
    if (availability === "on_leave") {
      list = list.filter((s) => timeOff.some((t) => t.staff_user_id === s.id && t.start_date <= weekEnd && t.end_date >= weekStart));
    }
    return list;
  }, [staff, roleFilter, query, timeOff, availability, weekStart, weekEnd]);

  const visitsFor = (staffId, day) =>
    visits
      .filter((v) => v.staff_user_id === staffId && (v.scheduled_start || "").startsWith(day))
      .filter((v) => shiftFilter === "all" ? true : shiftType(v) === shiftFilter)
      .filter((v) => filterSite === "all" ? true : v.site_id === filterSite)
      .sort((a, b) => (a.scheduled_start || "").localeCompare(b.scheduled_start || ""));

  const hasOverlap = (list) => {
    for (let i = 0; i < list.length; i++) for (let j = i + 1; j < list.length; j++) if (overlaps(list[i], list[j])) return true;
    return false;
  };

  const unassignedByDay = (day) =>
    visits.filter((v) => !v.staff_user_id && (v.scheduled_start || "").startsWith(day))
      .filter((v) => shiftFilter === "all" ? true : shiftType(v) === shiftFilter)
      .filter((v) => filterSite === "all" ? true : v.site_id === filterSite);

  // Service users authorised for the currently-selected shift site
  const clientsForShiftSite = (siteId) => {
    if (!siteId) return clients;
    return clients.filter((c) => {
      const primary = c.site_id;
      const additional = c.additional_site_ids || [];
      // If the client has no site metadata at all, allow (admin backward compat)
      if (!primary && additional.length === 0) return true;
      return primary === siteId || additional.includes(siteId);
    });
  };

  const isOff = (staffId, day) =>
    timeOff.find((t) => t.staff_user_id === staffId && t.start_date <= day && t.end_date >= day);

  const staffWeeklyHours = (staffId) =>
    visits.filter((v) => v.staff_user_id === staffId).reduce((sum, v) => sum + hoursOf(v), 0);

  // --- KPI dashboard ------------------------------------------------------
  const kpis = useMemo(() => {
    const uniqueStaff = new Set(visits.map((v) => v.staff_user_id).filter(Boolean));
    const todayVisits = visits.filter((v) => (v.scheduled_start || "").startsWith(today));
    const onDutyToday = new Set(todayVisits.filter((v) => v.staff_user_id).map((v) => v.staff_user_id)).size;
    const vacantShifts = visits.filter((v) => !v.staff_user_id).length;
    const annualLeave = timeOff.filter((t) => t.kind === "holiday").length;
    const sickLeave = timeOff.filter((t) => t.kind === "sick").length;
    let overtimeHours = 0;
    filteredStaff.forEach((s) => {
      const h = staffWeeklyHours(s.id);
      if (h > WEEKLY_HOURS_LIMIT) overtimeHours += (h - WEEKLY_HOURS_LIMIT);
    });
    const draftCount = visits.filter((v) => !v.published_at).length;
    return {
      scheduledStaff: uniqueStaff.size,
      onDutyToday,
      vacantShifts,
      annualLeave,
      sickLeave,
      overtimeHours: Math.round(overtimeHours * 10) / 10,
      draftCount,
    };
  }, [visits, timeOff, filteredStaff, today]);

  // --- Conflict detection ------------------------------------------------
  const conflicts = useMemo(() => {
    const out = [];
    filteredStaff.forEach((s) => {
      // Double booking
      const own = visits.filter((v) => v.staff_user_id === s.id);
      for (let i = 0; i < own.length; i++) for (let j = i + 1; j < own.length; j++) {
        if (overlaps(own[i], own[j])) {
          out.push({ kind: "double_booking", staff: s.name || s.email, day: own[i].scheduled_start?.slice(0, 10), detail: `${own[i].client_name} · ${own[j].client_name}` });
          break;
        }
      }
      // Excess hours
      const h = staffWeeklyHours(s.id);
      if (h > WEEKLY_HOURS_LIMIT) out.push({ kind: "excess_hours", staff: s.name || s.email, detail: `${h.toFixed(1)}h (${(h - WEEKLY_HOURS_LIMIT).toFixed(1)}h over)` });
      // Leave conflict
      const conflicting = own.filter((v) => timeOff.some((t) => t.staff_user_id === s.id && t.start_date <= (v.scheduled_start || "") && t.end_date >= (v.scheduled_start || "").slice(0, 10)));
      if (conflicting.length) out.push({ kind: "leave_conflict", staff: s.name || s.email, detail: `${conflicting.length} shift(s) during booked leave` });
    });
    return out.slice(0, 12);
  }, [filteredStaff, visits, timeOff]);

  // --- Actions ----------------------------------------------------------
  const publish = async () => {
    if (kpis.draftCount === 0) { toast.success("No draft changes to publish."); return; }
    if (!window.confirm(`Publish ${kpis.draftCount} draft change(s) and notify assigned staff?`)) return;
    setPublishing(true);
    try {
      const r = await api.post("/rota/publish", { start_date: weekStart, end_date: weekEnd });
      toast.success(`Published ${r.data.newly_published_count} shift(s) · notified ${r.data.carers_notified} carer(s)`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setPublishing(false); }
  };

  const copyWeek = async () => {
    if (!window.confirm("Copy this week's shifts into next week?")) return;
    try {
      const next = addDays(weekStart, 7);
      const source = visits.filter((v) => (v.scheduled_start || "").startsWith(weekStart) || days.some((d) => (v.scheduled_start || "").startsWith(d)));
      let count = 0;
      for (const v of source) {
        const dayIdx = days.findIndex((d) => (v.scheduled_start || "").startsWith(d));
        if (dayIdx < 0) continue;
        const newDay = addDays(next, dayIdx);
        const newStart = newDay + "T" + v.scheduled_start.slice(11);
        const newEnd = v.scheduled_end ? newDay + "T" + v.scheduled_end.slice(11) : "";
        await api.post("/visits", {
          data: {
            client_id: v.client_id,
            staff_user_id: v.staff_user_id,
            scheduled_start: newStart,
            scheduled_end: newEnd,
            notes: v.notes || "",
          },
        });
        count++;
      }
      toast.success(`Copied ${count} shift(s) to ${next}. They are DRAFT until you publish.`);
      setWeekStart(next);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const notifyStaff = async () => {
    toast.info("Notifications are sent automatically as part of Publish.");
  };

  const printRota = () => window.print();

  const exportCsv = () => {
    const rows = [["Staff", "Role", "Site", "Day", "Client", "Start", "End", "Hours", "Status", "Published"]];
    filteredStaff.forEach((s) => {
      days.forEach((d) => {
        visitsFor(s.id, d).forEach((v) => {
          rows.push([s.name || s.email, s.role, v.site_name || "", d, v.client_name || v.client_id, timeHM(v.scheduled_start), timeHM(v.scheduled_end), hoursOf(v).toFixed(1), v.status || "", v.published_at ? "Yes" : "Draft"]);
        });
      });
    });
    const csv = rows.map((r) => r.map((c) => `"${String(c || "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `rota-${weekStart}.csv`; a.click(); URL.revokeObjectURL(url);
  };

  const submitTimeOff = async () => {
    if (!toForm.staff_user_id || !toForm.start_date || !toForm.end_date) { toast.error("Staff, start and end date are required"); return; }
    setToSaving(true);
    try {
      await api.post("/staff-ext/time-off", toForm);
      toast.success("Time off recorded");
      setToOpen(false); setToForm({ staff_user_id: "", start_date: "", end_date: "", kind: "holiday", notes: "" });
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setToSaving(false); }
  };

  const shift = (n) => setWeekStart(addDays(weekStart, n * 7));

  // ---------------------------------------------------------------------
  // Batch 4.1 mobile / tablet navigation helpers (read-only view).
  // They reuse the SAME `weekStart` / `setWeekStart` used by desktop so
  // there is exactly one source of truth for the week, and one server
  // fetch pipeline. When the caller crosses a week boundary, weekStart
  // is nudged to the corresponding Monday (using existing `mondayOf`).
  // ---------------------------------------------------------------------
  const goPrevDay = () => {
    const prev = addDays(selectedDay, -1);
    const prevWeek = mondayOf(prev);
    if (prevWeek !== weekStart) setWeekStart(prevWeek);
    setSelectedDay(prev);
  };
  const goNextDay = () => {
    const next = addDays(selectedDay, 1);
    const nextWeek = mondayOf(next);
    if (nextWeek !== weekStart) setWeekStart(nextWeek);
    setSelectedDay(next);
  };
  const goToday = () => {
    const t = todayISO();
    const tWeek = mondayOf(t);
    if (tWeek !== weekStart) setWeekStart(tWeek);
    setSelectedDay(t);
  };
  const pickMobileWeek = (wk) => {
    setWeekStart(wk);
    // If the currently-selected day falls outside the newly-picked week,
    // snap it to the Monday of that week. Preserves day-of-week otherwise.
    const dayIdx = Array.from({ length: 7 }, (_, i) => addDays(wk, i)).indexOf(selectedDay);
    if (dayIdx < 0) setSelectedDay(wk);
  };

  // Compact 13-week heatmap window (6 back, current, 6 fwd).
  const heatmapWeeks = useMemo(
    () => Array.from({ length: 13 }, (_, i) => addDays(weekStart, (i - 6) * 7)),
    [weekStart],
  );

  // --- Inline Create Shift ------------------------------------------------
  const openCreate = (staffId, staffName, day, unassigned = false) => {
    const defaultSite = accessibleSites.length === 1 ? accessibleSites[0].id : (user?.site_id || "");
    const eligibleClients = clientsForShiftSite(defaultSite);
    setNewShift({
      staffId: unassigned ? null : staffId,
      staffName: unassigned ? "Unassigned" : staffName,
      day,
      unassigned,
      site_id: defaultSite,
      client_id: eligibleClients[0]?.id || "",
      start_time: "09:00",
      end_time: "10:00",
      notes: "",
    });
  };
  // When Site changes → filter Service User dropdown; when Service User
  // changes → auto-pin its primary site if it only belongs to one.
  const onNewShiftSiteChange = (siteId) => {
    setNewShift((prev) => {
      if (!prev) return prev;
      const eligible = clientsForShiftSite(siteId);
      const stillValid = eligible.find((c) => c.id === prev.client_id);
      return { ...prev, site_id: siteId, client_id: stillValid ? prev.client_id : (eligible[0]?.id || "") };
    });
  };
  const onNewShiftClientChange = (clientId) => {
    setNewShift((prev) => {
      if (!prev) return prev;
      const c = clients.find((x) => x.id === clientId);
      const clientSites = c ? [c.site_id, ...(c.additional_site_ids || [])].filter(Boolean) : [];
      // Auto-fill site iff client belongs to exactly one accessible site and
      // no site has been chosen yet (or the current pick doesn't match).
      let nextSite = prev.site_id;
      if (clientSites.length === 1 && accessibleSites.some((s) => s.id === clientSites[0])) {
        nextSite = clientSites[0];
      }
      return { ...prev, client_id: clientId, site_id: nextSite };
    });
  };
  const submitNewShift = async () => {
    if (!newShift) return;
    if (accessibleSites.length > 0 && !newShift.site_id) { toast.error("Pick a Site / House"); return; }
    if (!newShift.client_id) { toast.error("Pick a service user"); return; }
    if (!newShift.start_time) { toast.error("Start time is required"); return; }
    if (newShift.end_time && newShift.end_time <= newShift.start_time) {
      toast.error("End time must be after start time"); return;
    }
    setSavingShift(true);
    try {
      const payload = {
        data: {
          site_id: newShift.site_id || null,
          client_id: newShift.client_id,
          staff_user_id: newShift.staffId || null,
          scheduled_start: `${newShift.day}T${newShift.start_time}`,
          scheduled_end: newShift.end_time ? `${newShift.day}T${newShift.end_time}` : null,
          status: "scheduled",
          notes: newShift.notes.trim() || null,
        },
      };
      await api.post("/visits", payload);
      toast.success(newShift.unassigned ? "Vacant shift added — publish or drag onto a carer." : "Shift added — publish to notify the carer.");
      setNewShift(null);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingShift(false); }
  };

  // --- Site Coverage ------------------------------------------------------
  const loadCoverage = useCallback(async () => {
    setCoverageLoading(true);
    try {
      const end = addDays(weekStart, 6);
      const r = await api.get(`/visits/site-coverage?start_date=${weekStart}&end_date=${end}`);
      setCoverage(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setCoverageLoading(false); }
  }, [weekStart]);
  useEffect(() => {
    if (coverageOpen) loadCoverage();
  }, [coverageOpen, loadCoverage]);

  // --- Delete shift -------------------------------------------------------
  // Uses the existing backend endpoint DELETE /api/visits/{id} which already
  // enforces require_role("admin", "manager") + site_filter(user). No new
  // payroll/history/attendance rule is invented here — this only exposes
  // the capability the API has always supported.
  const handleDeleteShift = async () => {
    if (!askDeleteShift || !canManage) return;
    const shift = askDeleteShift;
    setDeletingShift(true);
    // Optimistic removal so KPIs update immediately even before reload.
    const prev = visits;
    setVisits((cur) => cur.filter((v) => v.id !== shift.id));
    try {
      await api.delete(`/visits/${shift.id}`);
      toast.success("Shift deleted");
      setAskDeleteShift(null);
      setViewShift(null);
      await load();
    } catch (e) {
      setVisits(prev);
      toast.error(formatApiError(e));
    } finally {
      setDeletingShift(false);
    }
  };

  // --- Drag & drop --------------------------------------------------------
  const handleDragStart = (event) => {
    const id = event.active?.id || "";
    if (!id.startsWith("shift:")) return;
    const vid = id.slice("shift:".length);
    const v = visits.find((x) => x.id === vid);
    if (v) setActiveDrag(v);
  };

  const handleDragEnd = async (event) => {
    setActiveDrag(null);
    if (!canManage) return;
    const active = event.active?.id;
    const over = event.over?.id;
    if (!active || !over || !active.startsWith("shift:")) return;

    const vid = active.slice("shift:".length);
    const v = visits.find((x) => x.id === vid);
    if (!v) return;

    // Parse drop target
    let newStaffId = null;
    let newDay = null;
    if (over.startsWith("staff:")) {
      const parts = over.split(":");
      // format: staff:<staffId>:day:<YYYY-MM-DD>
      newStaffId = parts[1];
      newDay = parts[3];
    } else if (over.startsWith("unassigned:")) {
      newStaffId = null;
      newDay = over.split(":")[2];
    } else return;

    if (!newDay) return;
    const currentDay = (v.scheduled_start || "").slice(0, 10);
    if (currentDay === newDay && (v.staff_user_id || null) === (newStaffId || null)) return;

    // Guard: cannot drop onto staff who is on leave that day
    if (newStaffId && isOff(newStaffId, newDay)) {
      toast.error("That carer is on leave that day — pick another day or carer.");
      return;
    }

    // Build new start/end preserving time-of-day
    const timeStart = (v.scheduled_start || "").slice(11) || "09:00";
    const timeEnd = v.scheduled_end ? v.scheduled_end.slice(11) : "";
    const newStart = `${newDay}T${timeStart}`;
    const newEnd = timeEnd ? `${newDay}T${timeEnd}` : "";

    // Optimistic update
    const prev = visits;
    setVisits((cur) => cur.map((x) => x.id === vid ? {
      ...x,
      staff_user_id: newStaffId,
      scheduled_start: newStart,
      scheduled_end: newEnd || x.scheduled_end,
      published_at: null,
    } : x));

    try {
      const payload = {
        data: {
          staff_user_id: newStaffId,
          scheduled_start: newStart,
          scheduled_end: newEnd || v.scheduled_end,
          published_at: null,
          acceptance_status: null,
        },
      };
      await api.put(`/visits/${vid}`, payload);
      toast.success(newStaffId ? "Shift reassigned — publish to notify staff." : "Shift moved to Unassigned.");
    } catch (e) {
      setVisits(prev);
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="flex-1 min-h-0 flex flex-col print:overflow-visible">
      {/* Desktop header (≥ lg). Hidden on mobile/tablet — the AppShell
          MobilePageHeader owns the top bar there. Desktop markup is
          unchanged from before Batch 4.1. */}
      <div className="h-14 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-6 hidden lg:flex items-center justify-between shrink-0 print:hidden">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Care Delivery</div>
          <h1 className="font-heading text-base font-semibold" data-testid="schedule-title">Staff Rota</h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center border border-slate-300 dark:border-slate-600 h-9 bg-white dark:bg-slate-900 rounded-md">
            <button onClick={() => shift(-1)} data-testid="schedule-prev" className="px-2 hover:bg-slate-50 dark:hover:bg-slate-800 h-full"><ChevronLeft className="w-4 h-4" /></button>
            <div className="px-2 text-xs flex items-center gap-1.5 border-x border-slate-300 dark:border-slate-600 h-full">
              <CalendarClock className="w-3.5 h-3.5 text-slate-500" />
              <input type="date" value={weekStart} onChange={(e) => setWeekStart(mondayOf(e.target.value))} data-testid="schedule-week-start" className="text-sm outline-none bg-transparent" />
            </div>
            <button onClick={() => shift(1)} data-testid="schedule-next" className="px-2 hover:bg-slate-50 dark:hover:bg-slate-800 h-full"><ChevronRight className="w-4 h-4" /></button>
          </div>
          {canManage && (
            <Button size="sm" variant="outline" onClick={() => setAiOpen(true)} data-testid="btn-ai-optimise" className="h-9 border-violet-300 text-violet-700 hover:bg-violet-50 dark:hover:bg-violet-950/30">
              <Sparkles className="w-3.5 h-3.5 mr-1.5" /> AI Optimise
            </Button>
          )}
          {canManage && (
            <Button size="sm" onClick={publish} disabled={publishing || kpis.draftCount === 0} data-testid="btn-publish-rota" className={`h-9 ${kpis.draftCount ? "bg-emerald-700 hover:bg-emerald-800 text-white" : ""}`}>
              <Send className="w-3.5 h-3.5 mr-1.5" />
              {publishing ? "Publishing…" : kpis.draftCount ? `Publish ${kpis.draftCount} change${kpis.draftCount === 1 ? "" : "s"}` : "All published"}
            </Button>
          )}
        </div>
      </div>

      {/* -----------------------------------------------------------------
          Batch 4.1 · MOBILE / TABLET view ( <lg ) — read-only agenda.
          Hidden on ≥ lg via component's own `lg:hidden` wrapper. Reuses
          the same state / handlers as desktop; introduces zero mutation
          workflows and zero recalculation. Payroll / attendance /
          rate-snapshot logic is not touched.
          --------------------------------------------------------------- */}
      <MobileStaffScheduleAgenda
        selectedDay={selectedDay}
        today={today}
        weekStart={weekStart}
        days={days}
        heatmapWeeks={heatmapWeeks}
        heatmap={heatmap}
        visits={visits}
        staff={staff}
        timeOff={timeOff}
        accessibleSites={accessibleSites}
        filterSite={filterSite}
        kpis={kpis}
        canManage={canManage}
        draftCount={kpis.draftCount}
        publishing={publishing}
        isOff={isOff}
        onPrevDay={goPrevDay}
        onNextDay={goNextDay}
        onGoToday={goToday}
        onPickWeek={pickMobileWeek}
        onSetFilterSite={setFilterSite}
        onViewShift={setViewShift}
        onOpenAi={() => setAiOpen(true)}
        onCopyWeek={copyWeek}
        onNotify={notifyStaff}
        onExport={exportCsv}
        onPrint={printRota}
        onOpenTimeOff={() => setToOpen(true)}
        onPublish={publish}
      />

      <div className="hidden lg:block flex-1 overflow-auto p-6 space-y-4 bg-slate-50 dark:bg-slate-950">
        {/* 13-week heatmap strip */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md px-3 py-2 print:hidden" data-testid="rota-heatmap">
          <div className="flex items-center gap-2 mb-1.5">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">13-Week Overview</div>
            <div className="text-[10px] text-slate-400">click any week to jump</div>
            <div className="ml-auto flex items-center gap-2 text-[9px] text-slate-500">
              <span className="inline-flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 bg-slate-100 border border-slate-300 rounded-sm" />0</span>
              <span className="inline-flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 bg-emerald-100 border border-emerald-300 rounded-sm" />1–2</span>
              <span className="inline-flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 bg-emerald-300 border border-emerald-500 rounded-sm" />3–7</span>
              <span className="inline-flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 bg-emerald-500 border border-emerald-700 rounded-sm" />8+</span>
            </div>
          </div>
          <div className="flex items-stretch gap-1" data-testid="rota-heatmap-strip">
            {heatmapWeeks.map((wk) => {
              const count = heatmap[wk] || 0;
              const isCurrent = wk === weekStart;
              const tone =
                count === 0 ? "bg-slate-50 border-slate-200 text-slate-400 hover:bg-slate-100" :
                count < 3 ? "bg-emerald-100 border-emerald-300 text-emerald-900 hover:bg-emerald-200" :
                count < 8 ? "bg-emerald-300 border-emerald-500 text-emerald-950 hover:bg-emerald-400" :
                "bg-emerald-500 border-emerald-700 text-white hover:bg-emerald-600";
              const wkDate = new Date(wk + "T00:00:00");
              return (
                <button
                  key={wk}
                  type="button"
                  onClick={() => setWeekStart(wk)}
                  data-testid={`heatmap-week-${wk}`}
                  className={`flex-1 min-w-0 flex flex-col items-center py-1.5 rounded-sm border transition-colors ${tone} ${isCurrent ? "ring-2 ring-sky-500 ring-offset-1 dark:ring-offset-slate-900" : ""}`}
                  title={`${wkDate.toLocaleDateString(undefined, { weekday: "short", day: "2-digit", month: "short", year: "numeric" })} · ${count} shift${count === 1 ? "" : "s"}`}
                >
                  <div className="text-[9px] uppercase tracking-widest opacity-70">{wkDate.toLocaleDateString(undefined, { month: "short" })}</div>
                  <div className="text-xs font-semibold tabular-nums leading-tight">{wkDate.getDate()}</div>
                  <div className="text-[10px] tabular-nums opacity-80">{count}</div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Site Coverage panel */}
        {accessibleSites.length > 0 && (
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md print:hidden" data-testid="rota-site-coverage">
            <button
              type="button"
              onClick={() => setCoverageOpen((v) => !v)}
              className="w-full px-3 py-2 flex items-center gap-2 text-left hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors"
              data-testid="btn-toggle-coverage"
              aria-expanded={coverageOpen}
            >
              <Building2 className="w-4 h-4 text-slate-600 dark:text-slate-300 shrink-0" />
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Site Coverage</div>
              {(() => {
                const shortTotal = coverage?.sites?.reduce((n, s) => n + s.rows.filter((r) => r.short).length, 0) ?? 0;
                if (!coverage) return <span className="text-[11px] text-slate-400">click to load coverage analysis</span>;
                return shortTotal > 0 ? (
                  <span className="inline-flex items-center gap-1 text-[11px] text-red-700 font-semibold" data-testid="coverage-short-count">
                    <AlertTriangle className="w-3 h-3" /> {shortTotal} block{shortTotal === 1 ? "" : "s"} below minimum
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700 font-semibold">
                    <UserCheck className="w-3 h-3" /> All blocks fully covered
                  </span>
                );
              })()}
              <ChevronDown className={`w-4 h-4 text-slate-400 ml-auto transition-transform ${coverageOpen ? "rotate-180" : ""}`} />
            </button>
            {coverageOpen && (
              <div className="border-t border-slate-100 dark:border-slate-800 p-3" data-testid="coverage-content">
                {coverageLoading ? (
                  <div className="text-sm text-slate-500">Loading coverage…</div>
                ) : !coverage || coverage.sites.length === 0 ? (
                  <div className="text-sm text-slate-500">No sites configured. Add sites first to see coverage.</div>
                ) : (
                  <div className="space-y-4">
                    {coverage.sites.map((site) => {
                      const rowsByDay = {};
                      site.rows.forEach((r) => { (rowsByDay[r.day] ||= []).push(r); });
                      const orderedDays = Object.keys(rowsByDay).sort();
                      const blocks = site.blocks_config || [];
                      const siteShort = site.rows.filter((r) => r.short).length;
                      return (
                        <div key={site.site_id} data-testid={`coverage-site-${site.site_id}`}>
                          <div className="flex items-center gap-2 mb-1.5">
                            <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">{site.site_name}</div>
                            {siteShort > 0 ? (
                              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-red-700 font-semibold" data-testid={`coverage-short-${site.site_id}`}>
                                <AlertTriangle className="w-3 h-3" /> {siteShort} short
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">
                                <UserCheck className="w-3 h-3" /> covered
                              </span>
                            )}
                            <div className="ml-auto text-[10px] text-slate-500">
                              {blocks.map((b) => <span key={b.key} className="ml-2">{b.label} <span className="tabular-nums opacity-70">{b.start}–{b.end}</span> · min {b.min_staff}</span>)}
                            </div>
                          </div>
                          <div className="overflow-x-auto">
                            <table className="min-w-full text-xs border border-slate-200 dark:border-slate-700">
                              <thead className="bg-slate-50 dark:bg-slate-800/60">
                                <tr>
                                  <th className="text-left px-2 py-1 font-semibold text-slate-600 dark:text-slate-300">Day</th>
                                  {blocks.map((b) => <th key={b.key} className="text-left px-2 py-1 font-semibold text-slate-600 dark:text-slate-300">{b.label}</th>)}
                                </tr>
                              </thead>
                              <tbody>
                                {orderedDays.map((d) => {
                                  const dt = new Date(d + "T00:00:00");
                                  const dayRows = rowsByDay[d];
                                  return (
                                    <tr key={d} className="border-t border-slate-100 dark:border-slate-800">
                                      <td className="px-2 py-1 whitespace-nowrap">
                                        <span className="font-medium">{dt.toLocaleDateString(undefined, { weekday: "short" })}</span>
                                        <span className="text-slate-500 ml-1">{dt.toLocaleDateString(undefined, { day: "2-digit", month: "short" })}</span>
                                      </td>
                                      {blocks.map((b) => {
                                        const r = dayRows.find((x) => x.block_key === b.key);
                                        if (!r) return <td key={b.key} className="px-2 py-1 text-slate-300">—</td>;
                                        return (
                                          <td key={b.key} className={`px-2 py-1 tabular-nums ${r.short ? "text-red-800 bg-red-50/60 dark:bg-red-950/30 font-semibold" : "text-emerald-800"}`} data-testid={`coverage-cell-${site.site_id}-${d}-${b.key}`}>
                                            <span className="inline-flex items-center gap-1">
                                              {r.short && <AlertTriangle className="w-3 h-3" />}
                                              {r.actual}/{r.min_staff}
                                              {r.short && <span className="text-[10px] uppercase tracking-widest ml-1">{Math.abs(r.delta)} short</span>}
                                            </span>
                                          </td>
                                        );
                                      })}
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* KPI dashboard */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="rota-kpis">
          <KpiCard icon={Users} label="Staff Scheduled" value={kpis.scheduledStaff} tone="info" testid="kpi-scheduled" />
          <KpiCard icon={UserCheck} label="On Duty Today" value={kpis.onDutyToday} tone="ok" testid="kpi-on-duty" />
          <KpiCard icon={UserX} label="Vacant Shifts" value={kpis.vacantShifts} tone={kpis.vacantShifts > 0 ? "warn" : "ok"} testid="kpi-vacant" />
          <KpiCard icon={Umbrella} label="Annual Leave" value={kpis.annualLeave} tone="info" testid="kpi-holiday" />
          <KpiCard icon={Heart} label="Sick Leave" value={kpis.sickLeave} tone={kpis.sickLeave > 0 ? "warn" : "ok"} testid="kpi-sick" />
          <KpiCard icon={TrendingUp} label="Overtime Hours" value={`${kpis.overtimeHours}h`} tone={kpis.overtimeHours > 0 ? "danger" : "ok"} testid="kpi-overtime" />
        </div>

        {/* Staff Hours & Payroll Cost — new feature (Feb 2026).
            Extends the rota; does not replace it. */}
        <PayrollWidget
          weekStart={weekStart}
          weekEnd={weekEnd}
          filterSite={filterSite}
          user={user}
          canManage={canManage}
        />

        {/* Filters & quick actions */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md p-3 flex items-center flex-wrap gap-2 print:hidden">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Search staff…" value={query} onChange={(e) => setQuery(e.target.value)} data-testid="rota-search" className="h-9 pl-8 w-56 text-sm bg-white dark:bg-slate-900" />
          </div>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="h-9 w-40 text-xs bg-white dark:bg-slate-900" data-testid="filter-role"><SelectValue placeholder="Role" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All roles</SelectItem><SelectItem value="care_worker">Care worker</SelectItem><SelectItem value="manager">Manager</SelectItem></SelectContent>
          </Select>
          <Select value={shiftFilter} onValueChange={setShiftFilter}>
            <SelectTrigger className="h-9 w-40 text-xs bg-white dark:bg-slate-900" data-testid="filter-shift"><SelectValue placeholder="Shift type" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All shifts</SelectItem><SelectItem value="morning">Morning</SelectItem><SelectItem value="afternoon">Afternoon</SelectItem><SelectItem value="evening">Evening</SelectItem><SelectItem value="night">Night</SelectItem></SelectContent>
          </Select>
          <Select value={filterSite} onValueChange={setFilterSite}>
            <SelectTrigger className="h-9 w-44 text-xs bg-white dark:bg-slate-900" data-testid="filter-site">
              <Building2 className="w-3.5 h-3.5 mr-1 text-slate-500 shrink-0" />
              <SelectValue placeholder="Site / House" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sites / houses</SelectItem>
              {accessibleSites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={availability} onValueChange={setAvailability}>
            <SelectTrigger className="h-9 w-40 text-xs bg-white dark:bg-slate-900" data-testid="filter-avail"><SelectValue placeholder="Availability" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All staff</SelectItem><SelectItem value="available">Available</SelectItem><SelectItem value="on_leave">On leave</SelectItem></SelectContent>
          </Select>
          <div className="flex-1" />
          {canManage && (<>
            <Button variant="outline" size="sm" onClick={copyWeek} data-testid="btn-copy-week" className="h-9"><Copy className="w-3.5 h-3.5 mr-1.5" /> Copy Week</Button>
            <Button variant="outline" size="sm" onClick={notifyStaff} data-testid="btn-notify-staff" className="h-9"><Send className="w-3.5 h-3.5 mr-1.5" /> Notify</Button>
            <Button variant="outline" size="sm" onClick={exportCsv} data-testid="btn-export-rota" className="h-9"><FileSpreadsheet className="w-3.5 h-3.5 mr-1.5" /> Export</Button>
            <Button variant="outline" size="sm" onClick={printRota} data-testid="btn-print-rota" className="h-9"><Printer className="w-3.5 h-3.5 mr-1.5" /> Print</Button>
            <Button size="sm" onClick={() => setToOpen(true)} data-testid="btn-add-time-off" className="h-9 bg-slate-900 hover:bg-slate-800"><Umbrella className="w-3.5 h-3.5 mr-1.5" /> Log leave</Button>
          </>)}
        </div>

        {/* Conflicts summary */}
        {conflicts.length > 0 && (
          <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-md p-3" data-testid="rota-conflicts">
            <div className="text-xs font-semibold text-red-800 dark:text-red-300 mb-1.5 flex items-center gap-1.5"><ShieldAlert className="w-3.5 h-3.5" /> Rota conflicts detected ({conflicts.length})</div>
            <ul className="text-[11px] text-red-800 dark:text-red-300 space-y-0.5">
              {conflicts.slice(0, 5).map((c, i) => (
                <li key={i} className="flex items-center gap-2" data-testid={`conflict-${i}`}>
                  <span className="inline-flex px-1.5 py-0.5 rounded bg-red-100 dark:bg-red-900/40 font-medium">{c.kind.replace(/_/g, " ")}</span>
                  <span className="font-medium">{c.staff}</span>
                  <span>· {c.detail}</span>
                </li>
              ))}
              {conflicts.length > 5 && <li className="italic">…and {conflicts.length - 5} more</li>}
            </ul>
          </div>
        )}

        {/* Grid */}
        {loading ? (
          <div className="text-sm text-slate-500" data-testid="schedule-loading">Loading rota…</div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
          {canManage && (
            <div className="text-[11px] text-slate-500 mb-2 flex items-center gap-1.5 print:hidden" data-testid="dnd-hint">
              <GripVertical className="w-3 h-3" /> Tip: drag any shift card between carers or days to reassign — changes save as DRAFT until you publish.
            </div>
          )}
          {visits.length === 0 && (
            <div
              className="mb-3 flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30 px-4 py-3 print:hidden"
              data-testid="rota-empty-week"
            >
              <AlertTriangle className="w-4 h-4 text-amber-700 dark:text-amber-300 mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-amber-900 dark:text-amber-200">
                  No shifts scheduled for this week ({new Date(weekStart + "T00:00:00").toLocaleDateString(undefined, { day: "2-digit", month: "short" })} – {new Date(weekEnd + "T00:00:00").toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" })})
                </div>
                <div className="text-[12px] text-amber-800 dark:text-amber-300 mt-0.5">
                  Drag-and-drop reassigns existing shift cards — there aren't any in this week yet.{" "}
                  {nearest
                    ? <>The nearest week with shifts starts <span className="font-medium">{new Date(nearest.weekStart + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", day: "2-digit", month: "short" })}</span>.</>
                    : <>Add a shift below by hovering over any day cell.</>}
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {nearest && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setWeekStart(nearest.weekStart)}
                      data-testid="btn-jump-nearest-week"
                      className="h-8 border-amber-300 text-amber-900 hover:bg-amber-100 dark:border-amber-700 dark:text-amber-200"
                    >
                      <CalendarClock className="w-3.5 h-3.5 mr-1.5" /> Jump to nearest week with shifts
                    </Button>
                  )}
                  {canManage && filteredStaff.length > 0 && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openCreate(filteredStaff[0].id, filteredStaff[0].name || filteredStaff[0].email, weekStart)}
                      data-testid="btn-add-first-shift"
                      className="h-8"
                    >
                      <Plus className="w-3.5 h-3.5 mr-1.5" /> Add first shift
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md overflow-x-auto" data-testid="schedule-grid">
            <table className="min-w-full text-xs">
              <thead className="sticky top-0 z-10 bg-slate-100 dark:bg-slate-800">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold text-slate-700 dark:text-slate-200 w-64 border-b border-slate-200 dark:border-slate-700">Staff · Weekly hours</th>
                  {days.map((d, i) => {
                    const dt = new Date(d + "T00:00:00");
                    const isToday = d === today;
                    return (
                      <th key={d} className={`px-2 py-2 text-left font-semibold text-slate-700 dark:text-slate-200 min-w-[150px] border-b border-slate-200 dark:border-slate-700 ${isToday ? "bg-sky-50 dark:bg-sky-950/30" : ""}`}>
                        <div className={isToday ? "text-sky-700 dark:text-sky-300" : ""}>{WEEKDAYS[i]} <span className="text-slate-500 font-normal">{dt.toLocaleDateString(undefined, { day: "2-digit", month: "short" })}</span></div>
                        <DailyStaffingIndicator visits={visits.filter((v) => (v.scheduled_start || "").startsWith(d))} />
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {filteredStaff.map((s) => {
                  const hours = staffWeeklyHours(s.id);
                  const over = hours > WEEKLY_HOURS_LIMIT;
                  return (
                    <tr key={s.id} className="border-b border-slate-100 dark:border-slate-800 align-top hover:bg-slate-50/50 dark:hover:bg-slate-800/30" data-testid={`schedule-row-${s.id}`}>
                      <td className="px-3 py-2 group relative">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 shrink-0">
                            <PersonAvatar person={s} size="sm" testid={`schedule-avatar-${s.id}`} />
                          </div>
                          <div className="min-w-0">
                            <div className="font-medium text-slate-900 dark:text-slate-100 truncate">{s.name || s.email}</div>
                            <div className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1">
                              {s.role.replace("_", " ")}
                              <span className={`ml-1 tabular-nums font-semibold ${over ? "text-red-600" : "text-slate-600 dark:text-slate-300"}`}>{hours.toFixed(1)}h</span>
                              {over && <AlertTriangle className="w-3 h-3 text-red-600" title="Over 48h working limit" />}
                            </div>
                          </div>
                        </div>
                        <StaffHoverCard staff={s} />
                      </td>
                      {days.map((d) => {
                        const off = isOff(s.id, d);
                        if (off) {
                          return (
                            <td key={d} className="px-2 py-2 border-l border-slate-100 dark:border-slate-800 bg-slate-100/60 dark:bg-slate-800/40" data-testid={`schedule-off-${s.id}-${d}`}>
                              <div className="text-[10px] uppercase tracking-widest text-slate-500 inline-flex items-center gap-1"><Umbrella className="w-3 h-3" /> {off.kind}</div>
                              {off.notes && <div className="text-[11px] text-slate-500 mt-1 truncate">{off.notes}</div>}
                            </td>
                          );
                        }
                        const dv = visitsFor(s.id, d);
                        const clash = hasOverlap(dv);
                        return (
                          <DroppableCell
                            key={d}
                            id={`staff:${s.id}:day:${d}`}
                            testid={`schedule-cell-${s.id}-${d}`}
                            className={`px-2 py-2 border-l border-slate-100 dark:border-slate-800`}
                          >
                            {dv.length === 0 ? (
                              canManage ? (
                                <button
                                  type="button"
                                  onClick={() => openCreate(s.id, s.name || s.email, d)}
                                  className="w-full text-slate-300 hover:text-emerald-600 text-[11px] h-14 flex items-center justify-center gap-1 rounded border border-dashed border-slate-200 dark:border-slate-700 hover:border-emerald-400 hover:bg-emerald-50/40 dark:hover:bg-emerald-950/20 transition-colors group/cell"
                                  data-testid={`btn-add-shift-${s.id}-${d}`}
                                  title={`Add shift for ${s.name || s.email} on ${d}`}
                                >
                                  <Plus className="w-3.5 h-3.5 opacity-0 group-hover/cell:opacity-100 transition-opacity" />
                                  <span className="opacity-60 group-hover/cell:opacity-100">Add</span>
                                </button>
                              ) : (
                                <div className="text-slate-300 text-[11px] h-14 flex items-center justify-center rounded border border-dashed border-slate-200 dark:border-slate-700">—</div>
                              )
                            ) : (
                              <div className="space-y-1">
                                {clash && (
                                  <div className="text-[10px] uppercase tracking-widest text-red-700 inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> overlap</div>
                                )}
                                {dv.map((v) => <DraggableShift key={v.id} visit={v} conflict={clash} disabled={!canManage} onOpen={setViewShift} />)}
                              </div>
                            )}
                          </DroppableCell>
                        );
                      })}
                    </tr>
                  );
                })}
                {/* Unassigned row */}
                <tr className="border-t-2 border-slate-200 dark:border-slate-700 align-top bg-amber-50/40 dark:bg-amber-950/20" data-testid="schedule-unassigned-row">
                  <td className="px-3 py-2">
                    <div className="font-medium text-amber-800 dark:text-amber-300 flex items-center gap-1"><UserX className="w-3.5 h-3.5" /> Unassigned</div>
                    <div className="text-[10px] uppercase tracking-widest text-amber-700 dark:text-amber-400">Vacant shifts</div>
                  </td>
                  {days.map((d) => {
                    const un = unassignedByDay(d);
                    return (
                      <DroppableCell
                        key={d}
                        id={`unassigned:day:${d}`}
                        testid={`schedule-unassigned-${d}`}
                        className="px-2 py-2 border-l border-slate-100 dark:border-slate-800"
                      >
                        {un.length === 0 ? (
                          canManage ? (
                            <button
                              type="button"
                              onClick={() => openCreate(null, "Unassigned", d, true)}
                              className="w-full text-amber-400 hover:text-amber-700 text-[11px] h-10 flex items-center justify-center gap-1 rounded border border-dashed border-amber-200 hover:border-amber-500 hover:bg-amber-100/60 transition-colors group/cell"
                              data-testid={`btn-add-vacant-${d}`}
                              title={`Add vacant shift on ${d}`}
                            >
                              <Plus className="w-3.5 h-3.5 opacity-0 group-hover/cell:opacity-100 transition-opacity" />
                              <span className="opacity-60 group-hover/cell:opacity-100">Vacant</span>
                            </button>
                          ) : (
                            <span className="text-slate-300 text-[11px]">—</span>
                          )
                        ) : (
                          <div className="space-y-1">
                            {un.map((v) => <DraggableShift key={v.id} visit={v} unassigned disabled={!canManage} onOpen={setViewShift} />)}
                          </div>
                        )}
                      </DroppableCell>
                    );
                  })}
                </tr>
              </tbody>
            </table>
            {/* Legend */}
            <div className="text-[11px] text-slate-500 px-3 py-2 border-t border-slate-100 dark:border-slate-800 flex flex-wrap gap-3 print:hidden">
              <LegendChip cls="bg-amber-50 border-amber-300" label="Morning 06–12" />
              <LegendChip cls="bg-sky-50 border-sky-300" label="Afternoon 12–17" />
              <LegendChip cls="bg-violet-50 border-violet-300" label="Evening 17–22" />
              <LegendChip cls="bg-slate-800 border-slate-900" label="Night 22–06" text="text-white" />
              <LegendChip cls="bg-red-50 border-red-300" label="Overlap / conflict" />
              <LegendChip cls="bg-amber-50 border-amber-300 border-dashed" label="Vacant" />
              <LegendChip cls="bg-slate-100 border-slate-300" label="Time off" />
              <LegendChip cls="bg-white border-slate-400 border-dashed" label="Draft (unpublished)" />
            </div>
          </div>
          <DragOverlay dropAnimation={null}>
            {activeDrag ? (
              <div className="scale-105 opacity-95 shadow-lg pointer-events-none">
                <ShiftCard visit={activeDrag} conflict={false} />
              </div>
            ) : null}
          </DragOverlay>
          </DndContext>
        )}
      </div>

      {/* AI Optimiser modal */}
      <RotaAiOptimiser
        open={aiOpen}
        onOpenChange={setAiOpen}
        weekStart={weekStart}
        weekEnd={weekEnd}
        onApplied={load}
      />

      {/* Time off dialog (retained from previous version) */}
      <Dialog open={toOpen} onOpenChange={setToOpen}>
        <DialogContent data-testid="time-off-dialog">
          <DialogHeader>
            <DialogTitle>Log time off</DialogTitle>
            <DialogDescription>Holiday, sickness, training. Days shown here are skipped by the rota generator.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Staff</Label><Select value={toForm.staff_user_id} onValueChange={(v) => setToForm({ ...toForm, staff_user_id: v })}><SelectTrigger data-testid="time-off-staff"><SelectValue placeholder="Select…" /></SelectTrigger><SelectContent>{staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name || s.email}</SelectItem>)}</SelectContent></Select></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>From</Label><Input type="date" value={toForm.start_date} onChange={(e) => setToForm({ ...toForm, start_date: e.target.value })} data-testid="time-off-start" /></div>
              <div><Label>To</Label><Input type="date" value={toForm.end_date} onChange={(e) => setToForm({ ...toForm, end_date: e.target.value })} data-testid="time-off-end" /></div>
            </div>
            <div><Label>Kind</Label><Select value={toForm.kind} onValueChange={(v) => setToForm({ ...toForm, kind: v })}><SelectTrigger data-testid="time-off-kind"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="holiday">Holiday</SelectItem><SelectItem value="sick">Sickness</SelectItem><SelectItem value="training">Training</SelectItem><SelectItem value="other">Other</SelectItem></SelectContent></Select></div>
            <div><Label>Notes</Label><Textarea rows={2} value={toForm.notes} onChange={(e) => setToForm({ ...toForm, notes: e.target.value })} data-testid="time-off-notes" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setToOpen(false)}>Cancel</Button>
            <Button onClick={submitTimeOff} disabled={toSaving} data-testid="btn-save-time-off" className="bg-slate-900 hover:bg-slate-800">{toSaving ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Inline Create Shift dialog */}
      <Dialog open={!!newShift} onOpenChange={(v) => { if (!v) setNewShift(null); }}>
        <DialogContent data-testid="create-shift-dialog">
          <DialogHeader>
            <DialogTitle>New shift</DialogTitle>
            <DialogDescription>
              {newShift ? (
                <>
                  {newShift.unassigned ? "Add a vacant shift on " : `Assign a visit to ${newShift.staffName} on `}
                  <span className="font-medium">{new Date(newShift.day + "T00:00:00").toLocaleDateString(undefined, { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}</span>. Saves as DRAFT until you publish.
                </>
              ) : null}
            </DialogDescription>
          </DialogHeader>
          {newShift && (
            <div className="space-y-3">
              <div>
                <Label className="flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5" /> Site / House {accessibleSites.length > 0 && <span className="text-red-600">*</span>}</Label>
                {accessibleSites.length === 1 ? (
                  <div className="mt-1 text-sm px-3 py-2 bg-slate-50 border border-slate-200 rounded" data-testid="create-shift-site-locked">
                    {accessibleSites[0].name} <span className="text-[10px] uppercase tracking-widest text-slate-500 ml-1">only site</span>
                  </div>
                ) : (
                  <Select value={newShift.site_id} onValueChange={onNewShiftSiteChange}>
                    <SelectTrigger data-testid="create-shift-site"><SelectValue placeholder={accessibleSites.length ? "Pick site / house" : "No sites configured"} /></SelectTrigger>
                    <SelectContent>
                      {accessibleSites.length === 0 && <SelectItem value="__none" disabled>No sites configured</SelectItem>}
                      {accessibleSites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              </div>
              <div>
                <Label>Service user</Label>
                <Select value={newShift.client_id} onValueChange={onNewShiftClientChange}>
                  <SelectTrigger data-testid="create-shift-client"><SelectValue placeholder="Pick service user" /></SelectTrigger>
                  <SelectContent>
                    {(() => {
                      const eligible = clientsForShiftSite(newShift.site_id);
                      if (eligible.length === 0) return <SelectItem value="__none" disabled>No service users at this site</SelectItem>;
                      return eligible.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.full_name}
                          {c.additional_site_ids?.length ? <span className="text-[10px] text-slate-400 ml-1">multi-site</span> : null}
                        </SelectItem>
                      ));
                    })()}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Start time</Label>
                  <Input type="time" value={newShift.start_time} onChange={(e) => setNewShift({ ...newShift, start_time: e.target.value })} data-testid="create-shift-start" />
                </div>
                <div>
                  <Label>End time</Label>
                  <Input type="time" value={newShift.end_time} onChange={(e) => setNewShift({ ...newShift, end_time: e.target.value })} data-testid="create-shift-end" />
                </div>
              </div>
              <div>
                <Label>Notes (optional)</Label>
                <Textarea rows={2} value={newShift.notes} onChange={(e) => setNewShift({ ...newShift, notes: e.target.value })} data-testid="create-shift-notes" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewShift(null)}>Cancel</Button>
            <Button onClick={submitNewShift} disabled={savingShift} data-testid="btn-save-new-shift" className="bg-emerald-700 hover:bg-emerald-800 text-white">
              {savingShift ? "Saving…" : "Create shift"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Batch 4.1 · Read-only View Shift dialog (mobile). Shows the same
          data desktop already renders on the shift card, without any
          mutation controls (no reassign / remove / edit attendance). */}
      <Dialog open={!!viewShift} onOpenChange={(v) => { if (!v) setViewShift(null); }}>
        <DialogContent data-testid="mobile-view-shift-dialog">
          <DialogHeader>
            <DialogTitle>Shift details</DialogTitle>
            <DialogDescription>
              {viewShift
                ? new Date((viewShift.scheduled_start || "").slice(0, 10) + "T00:00:00")
                    .toLocaleDateString(undefined, { weekday: "long", day: "2-digit", month: "long", year: "numeric" })
                : ""}
            </DialogDescription>
          </DialogHeader>
          {viewShift && (() => {
            const carer = staff.find((s) => s.id === viewShift.staff_user_id);
            const unassigned = !viewShift.staff_user_id;
            const draft = !viewShift.published_at;
            const kind = shiftType(viewShift);
            return (
              <div className="space-y-3 text-sm" data-testid="mobile-view-shift-body">
                {unassigned ? (
                  <div className="rounded-md border-2 border-dashed border-amber-400 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-700 p-3 flex items-center gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-700 dark:text-amber-400 shrink-0" />
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-amber-800 dark:text-amber-300 font-bold">
                        Vacant — staff required
                      </div>
                      <div className="text-xs text-amber-800 dark:text-amber-300">
                        Assign this shift from the desktop rota.
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 shrink-0">
                      <PersonAvatar person={carer || { name: "Staff" }} size="md" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-900 dark:text-slate-100 truncate" data-testid="mobile-view-shift-staff">
                        {carer?.name || carer?.email || "Assigned"}
                      </div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-500">
                        {carer?.role ? carer.role.replace(/_/g, " ") : "Care worker"}
                      </div>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="rounded-md border border-slate-200 dark:border-slate-700 p-2">
                    <div className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold">Time</div>
                    <div className="tabular-nums font-semibold text-slate-900 dark:text-slate-100 mt-0.5" data-testid="mobile-view-shift-time">
                      {timeHM(viewShift.scheduled_start)}{viewShift.scheduled_end ? `–${timeHM(viewShift.scheduled_end)}` : ""}
                    </div>
                  </div>
                  <div className="rounded-md border border-slate-200 dark:border-slate-700 p-2">
                    <div className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold">Block</div>
                    <div className="font-semibold text-slate-900 dark:text-slate-100 mt-0.5 capitalize">{SHIFT_LABEL[kind] || kind}</div>
                  </div>
                </div>

                {viewShift.client_name && (
                  <div className="rounded-md border border-slate-200 dark:border-slate-700 p-2">
                    <div className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold">Service user</div>
                    <div className="font-medium text-slate-900 dark:text-slate-100 mt-0.5 truncate" data-testid="mobile-view-shift-client">
                      {viewShift.client_name}
                    </div>
                  </div>
                )}

                {viewShift.site_name && (
                  <div className="rounded-md border border-slate-200 dark:border-slate-700 p-2">
                    <div className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold">Site / House</div>
                    <div className="font-medium text-slate-900 dark:text-slate-100 mt-0.5 truncate inline-flex items-center gap-1" data-testid="mobile-view-shift-site">
                      <Building2 className="w-3.5 h-3.5" /> {viewShift.site_name}
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-2 flex-wrap text-[11px]">
                  <span className={`px-2 py-1 rounded-md font-semibold uppercase tracking-widest ${
                    draft
                      ? "bg-slate-100 text-slate-700 border border-slate-300 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-600"
                      : "bg-emerald-50 text-emerald-800 border border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-300 dark:border-emerald-800"
                  }`} data-testid="mobile-view-shift-publish-state">
                    {draft ? "Draft" : "Published"}
                  </span>
                  {viewShift.status === "completed" && (
                    <span className="px-2 py-1 rounded-md font-semibold uppercase tracking-widest bg-emerald-50 text-emerald-800 border border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-300 dark:border-emerald-800">
                      Completed
                    </span>
                  )}
                  {viewShift.status === "missed" && (
                    <span className="px-2 py-1 rounded-md font-semibold uppercase tracking-widest bg-red-50 text-red-800 border border-red-200 dark:bg-red-950/30 dark:text-red-300 dark:border-red-800">
                      Missed
                    </span>
                  )}
                  {viewShift.check_in_at && (
                    <span className="px-2 py-1 rounded-md font-semibold uppercase tracking-widest bg-sky-50 text-sky-800 border border-sky-200 dark:bg-sky-950/30 dark:text-sky-300 dark:border-sky-800 tabular-nums" data-testid="mobile-view-shift-checkin">
                      In · {timeHM(viewShift.check_in_at)}
                    </span>
                  )}
                  {viewShift.check_out_at && (
                    <span className="px-2 py-1 rounded-md font-semibold uppercase tracking-widest bg-slate-50 text-slate-700 border border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-600 tabular-nums" data-testid="mobile-view-shift-checkout">
                      Out · {timeHM(viewShift.check_out_at)}
                    </span>
                  )}
                </div>

                {viewShift.notes && (
                  <div className="rounded-md border border-slate-200 dark:border-slate-700 p-2">
                    <div className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold">Notes</div>
                    <div className="text-xs text-slate-700 dark:text-slate-300 mt-0.5 whitespace-pre-wrap">{viewShift.notes}</div>
                  </div>
                )}

                <div className="text-[10px] text-slate-400 dark:text-slate-500 leading-snug">
                  Read-only view. Reassignments, attendance edits and shift edits are managed from the desktop rota.
                </div>
              </div>
            );
          })()}
          <DialogFooter className="gap-2 sm:gap-2">
            {canManage && viewShift && (
              <Button
                variant="outline"
                onClick={() => setAskDeleteShift(viewShift)}
                data-testid="btn-delete-shift"
                className="h-11 w-full sm:w-auto text-red-700 hover:text-red-800 hover:bg-red-50 border-red-200 dark:text-red-300 dark:border-red-900 dark:hover:bg-red-950/40"
              >
                <Trash2 className="w-4 h-4 mr-1.5" /> Delete shift
              </Button>
            )}
            <Button variant="outline" onClick={() => setViewShift(null)} data-testid="mobile-view-shift-close" className="h-11 w-full sm:w-auto">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete-shift confirmation. Managers/Admins only — the button
          that opens this is not rendered for other roles, and the backend
          endpoint independently enforces RBAC + site scope. */}
      <Dialog open={!!askDeleteShift} onOpenChange={(v) => { if (!v && !deletingShift) setAskDeleteShift(null); }}>
        <DialogContent data-testid="delete-shift-dialog">
          <DialogHeader>
            <DialogTitle>Delete this shift?</DialogTitle>
            <DialogDescription>
              This will remove the shift from the rota.
            </DialogDescription>
          </DialogHeader>
          {askDeleteShift?.published_at && (
            <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800 flex items-start gap-2" data-testid="delete-shift-published-warning">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <div>
                This shift was already <span className="font-semibold">published</span> to the carer. Any attendance records or payroll references already recorded against this shift are preserved and will show as unattributed.
              </div>
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => setAskDeleteShift(null)}
              disabled={deletingShift}
              data-testid="btn-delete-shift-cancel"
              className="h-11 w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteShift}
              disabled={deletingShift}
              data-testid="btn-delete-shift-confirm"
              className="h-11 w-full sm:w-auto bg-red-600 hover:bg-red-700 text-white"
            >
              {deletingShift ? "Deleting…" : "Delete shift"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// --- Sub-components -----------------------------------------------------
function KpiCard({ icon: Icon, label, value, tone, testid }) {
  const tones = {
    ok: "border-emerald-200 bg-white text-emerald-800 dark:bg-slate-900 dark:border-emerald-800",
    warn: "border-amber-200 bg-white text-amber-800 dark:bg-slate-900 dark:border-amber-800",
    danger: "border-red-200 bg-white text-red-800 dark:bg-slate-900 dark:border-red-800",
    info: "border-slate-200 bg-white text-slate-800 dark:bg-slate-900 dark:border-slate-700",
  };
  return (
    <div className={`border rounded-md p-2.5 flex items-center gap-2.5 ${tones[tone]}`} data-testid={testid}>
      <Icon className="w-4 h-4 shrink-0 opacity-70" />
      <div>
        <div className="text-[9px] uppercase tracking-widest opacity-75 font-semibold">{label}</div>
        <div className="text-lg font-heading font-semibold tabular-nums leading-tight dark:text-slate-100">{value}</div>
      </div>
    </div>
  );
}

function ShiftCard({ visit, conflict, unassigned }) {
  const kind = shiftType(visit);
  const style = unassigned ? "bg-amber-50 border-amber-400 border-dashed text-amber-900" : (conflict ? "bg-red-50 border-red-400 text-red-900" : SHIFT_STYLE[kind]);
  const draft = !visit.published_at;
  const clientPerson = {
    name: visit.client_name,
    photo_thumb_url: visit.client_photo_thumb_url,
    photo_tile_url: visit.client_photo_tile_url,
    photo_url: visit.client_photo_url,
  };
  return (
    <div className={`p-1.5 border ${style} rounded-md text-[11px] transition-transform hover:scale-[1.02] hover:shadow-sm`} data-testid={`shift-card-${visit.id}`}>
      <div className="flex items-center gap-1.5">
        <PersonAvatar person={clientPerson} name={visit.client_name || "Unassigned"} size="xs" testid={`shift-avatar-${visit.id}`} />
        <div className="flex-1 min-w-0 flex items-center justify-between gap-1">
          <div className="font-medium truncate">{visit.client_name || visit.client_id || "Unassigned"}</div>
          {draft && <span className="text-[8px] uppercase font-bold px-1 border border-current rounded opacity-70">Draft</span>}
        </div>
      </div>
      {visit.site_name && (
        <div className="flex items-center gap-1 mt-0.5 opacity-80" data-testid={`shift-site-${visit.id}`}>
          <Building2 className="w-2.5 h-2.5 shrink-0" />
          <div className="truncate text-[10px]">{visit.site_name}</div>
        </div>
      )}
      <div className="flex items-center justify-between opacity-90 mt-0.5">
        <div className="tabular-nums">{timeHM(visit.scheduled_start)}{visit.scheduled_end ? `–${timeHM(visit.scheduled_end)}` : ""}</div>
        <div className="text-[9px] uppercase tracking-widest opacity-80">{SHIFT_LABEL[kind] || kind}</div>
      </div>
      {visit.status === "completed" && <div className="text-[9px] text-emerald-700 font-semibold">✓ completed</div>}
      {visit.status === "missed" && <div className="text-[9px] text-red-700 font-semibold">✗ missed</div>}
    </div>
  );
}

function DailyStaffingIndicator({ visits }) {
  const assigned = visits.filter((v) => v.staff_user_id).length;
  const total = visits.length;
  const vacant = total - assigned;
  if (total === 0) return <div className="text-[9px] text-slate-400 mt-0.5">no shifts</div>;
  return (
    <div className="text-[9px] mt-0.5 text-slate-500 flex items-center gap-1.5">
      <span className="inline-flex items-center gap-0.5"><Users className="w-2.5 h-2.5" /> {assigned}/{total}</span>
      {vacant > 0 && <span className="inline-flex items-center gap-0.5 text-amber-700 font-medium"><UserX className="w-2.5 h-2.5" /> {vacant} vacant</span>}
    </div>
  );
}

function StaffHoverCard({ staff }) {
  return (
    <div className="hidden group-hover:block absolute z-20 left-4 top-full mt-1 w-56 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md shadow-lg p-3 text-[11px] print:hidden">
      <div className="font-semibold text-slate-900 dark:text-slate-100">{staff.name || staff.email}</div>
      <div className="text-slate-500 uppercase tracking-widest text-[9px] mt-0.5">{staff.role.replace("_", " ")}</div>
      {staff.email && <div className="mt-1 text-slate-600 dark:text-slate-300 truncate">{staff.email}</div>}
      {staff.skills?.length > 0 && (
        <div className="mt-2">
          <div className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold">Skills</div>
          <div className="flex flex-wrap gap-1 mt-0.5">{staff.skills.slice(0, 6).map((sk) => <span key={sk} className="px-1.5 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-200 text-[10px]">{sk}</span>)}</div>
        </div>
      )}
      {staff.training?.length > 0 && (
        <div className="mt-2">
          <div className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold">Training</div>
          <ul className="mt-0.5 text-slate-700 dark:text-slate-300 space-y-0.5">
            {staff.training.slice(0, 4).map((t) => <li key={t}>· {t}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}

function LegendChip({ cls, label, text }) {
  return <span className={`inline-flex items-center gap-1 ${text || ""}`}><span className={`inline-block w-3 h-3 border rounded-sm ${cls}`} /> {label}</span>;
}

// --- Drag & drop wrappers ------------------------------------------------
function DroppableCell({ id, testid, className, children }) {
  const { setNodeRef, isOver } = useDroppable({ id });
  const ring = isOver ? "ring-2 ring-violet-400 ring-inset bg-violet-50/50 dark:bg-violet-950/20" : "";
  return (
    <td ref={setNodeRef} className={`${className} ${ring}`} data-testid={testid}>
      {children}
    </td>
  );
}

function DraggableShift({ visit, conflict, unassigned, disabled, onOpen }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: `shift:${visit.id}`, disabled });
  // Plain click (no ≥6px drag) opens the shift dialog so managers can
  // access the Delete Shift action. dnd-kit's PointerSensor activation
  // distance (see StaffSchedule.jsx `sensors`) prevents a click from
  // being interpreted as a drag; a real drag never fires `onClick`.
  const handleClick = () => { if (!isDragging && onOpen) onOpen(visit); };
  return (
    <div
      ref={setNodeRef}
      style={{ opacity: isDragging ? 0.35 : 1 }}
      {...(disabled ? {} : listeners)}
      {...attributes}
      onClick={handleClick}
      role={onOpen ? "button" : undefined}
      tabIndex={onOpen ? 0 : undefined}
      onKeyDown={(e) => { if (onOpen && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); onOpen(visit); } }}
      className={disabled ? "cursor-pointer" : "cursor-grab active:cursor-grabbing"}
      data-testid={`draggable-shift-${visit.id}`}
    >
      <ShiftCard visit={visit} conflict={conflict} unassigned={unassigned} />
    </div>
  );
}
